import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, LinearProgress, Link, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography,
} from '@mui/material';
import { v4API } from '../../Api';
import { useV4 } from './V4Layout';
import { dateTime, errorMessage, formatMoney, StatusChip } from './common';

const emptyFilters = {
  status: '', merchantId: '', processingId: '', customerId: '', orderId: '',
  dateFrom: '', dateTo: '', reviewRequired: '',
};
const scalar = value => ['string', 'number', 'boolean'].includes(typeof value) ? String(value) : '—';
const statuses = [
  ['CREATING', 'Создаётся'], ['AWAITING_PAYMENT', 'Ожидает оплаты'], ['PAID', 'Оплачен'],
  ['CANCEL_PENDING', 'Ожидает отмены'], ['CANCELED', 'Отменён'], ['FAILED', 'Ошибка'], ['EXPIRED', 'Истёк'],
];
const orderTypes = { deposit: 'Депозит', purchase: 'Покупка', buybalance: 'Покупка с баланса', depsite: 'Depsite' };

export default function Orders() {
  const { system } = useV4();
  const ready = system?.ready === true;
  const [draft, setDraft] = useState(emptyFilters);
  const [query, setQuery] = useState({ page: 1, limit: 25 });
  const [result, setResult] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterError, setFilterError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (!ready) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    v4API.orders(query).then(data => {
      if (active) setResult({
        items: Array.isArray(data?.items) ? data.items : [],
        total: Number.isSafeInteger(data?.total) && data.total >= 0 ? data.total : 0,
      });
    }).catch(err => {
      if (active) { setError(errorMessage(err)); setResult({ items: [], total: 0 }); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ready, query, reload]);

  const change = event => setDraft(current => ({ ...current, [event.target.name]: event.target.value }));
  const apply = event => {
    event.preventDefault();
    if (draft.dateFrom && draft.dateTo && draft.dateFrom > draft.dateTo) {
      setFilterError('Начало периода не может быть позже окончания.');
      return;
    }
    setFilterError('');
    const filters = {};
    Object.entries(draft).forEach(([key, value]) => {
      if (value.trim()) filters[key] = key === 'reviewRequired' ? value === 'true' : value.trim();
    });
    if (filters.dateFrom) filters.dateFrom += 'T00:00:00.000Z';
    if (filters.dateTo) filters.dateTo += 'T23:59:59.999Z';
    setQuery({ ...filters, page: 1, limit: query.limit });
  };

  if (!ready) return <Alert severity="warning">V4 не готова. Список заказов недоступен до завершения настройки системы.</Alert>;

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="h4">Заказы V4</Typography>
        <Button onClick={() => setReload(value => value + 1)} disabled={loading}>Обновить</Button>
      </Stack>
      <Alert severity="info">Только заказы V4. Заказы старой системы здесь не отображаются.</Alert>
      <Paper component="form" onSubmit={apply} sx={{ p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField select label="Статус" name="status" value={draft.status} onChange={change} size="small">
            <MenuItem value="">Все статусы</MenuItem>
            {statuses.map(([status, label]) => <MenuItem key={status} value={status}>{label}</MenuItem>)}
          </TextField>
          {[
            ['merchantId', 'ID мерчанта'], ['processingId', 'ID процессинга'],
            ['orderId', 'ID заказа'], ['customerId', 'ID клиента'],
          ].map(([name, label]) => <TextField key={name} label={label} name={name} value={draft[name]} onChange={change} size="small" />)}
          <TextField select label="Ручная проверка" name="reviewRequired" value={draft.reviewRequired} onChange={change} size="small">
            <MenuItem value="">Все заказы</MenuItem><MenuItem value="true">Требуется</MenuItem><MenuItem value="false">Не требуется</MenuItem>
          </TextField>
          {[['dateFrom', 'Дата с (UTC)'], ['dateTo', 'Дата по (UTC)']].map(([name, label]) => (
            <TextField key={name} type="date" label={label} name={name} value={draft[name]} onChange={change} size="small" InputLabelProps={{ shrink: true }} />
          ))}
        </Box>
        {filterError && <Alert severity="error" sx={{ mt: 2 }}>{filterError}</Alert>}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" type="submit" disabled={loading}>Применить</Button>
          <Button disabled={loading} onClick={() => {
            setDraft(emptyFilters); setFilterError(''); setQuery({ page: 1, limit: query.limit });
          }}>Сбросить</Button>
        </Stack>
      </Paper>
      {error && <Alert severity="error" action={<Button color="inherit" onClick={() => setReload(value => value + 1)} disabled={loading}>Повторить</Button>}>{error}</Alert>}
      <Paper>
        {loading && <LinearProgress aria-label="Загрузка заказов" />}
        <TableContainer>
          <Table size="small" aria-label="Заказы V4" sx={{ minWidth: 1000 }}>
            <TableHead><TableRow>
              {['Создан / ID заказа', 'Мерчант / клиент', 'Процессинг', 'Тип', 'К оплате / к зачислению', 'Статус', 'Документы'].map(label => <TableCell key={label}>{label}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>
              {!loading && result.items.length === 0 && <TableRow><TableCell colSpan={7}><Typography sx={{ py: 3 }} color="text.secondary">Заказы не найдены</Typography></TableCell></TableRow>}
              {result.items.map((order, index) => <TableRow key={scalar(order._id) + index} hover>
                <TableCell>
                  <Typography variant="body2">{dateTime(order.createdAt)}</Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    <Link component={RouterLink} to={`/v4/orders/${encodeURIComponent(scalar(order._id))}`}>{scalar(order._id)}</Link>
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{scalar(order.merchantName)}</Typography>
                  <Typography variant="caption" component="div" fontFamily="monospace">{scalar(order.merchantId)}</Typography>
                  <Typography variant="caption" fontFamily="monospace">Клиент: {scalar(order.customerId)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{scalar(order.processingName)}</Typography>
                  <Typography variant="caption" component="div" fontFamily="monospace">{scalar(order.processingId)}</Typography>
                  <Typography variant="caption">{scalar(order.method)}</Typography>
                </TableCell>
                <TableCell>
                  {typeof order.type === 'string' && order.type
                    ? <Chip size="small" variant="outlined" label={orderTypes[order.type] || order.type} />
                    : <Typography variant="body2">—</Typography>}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatMoney(order.payableUnits, order.scale, order.currency)}</Typography>
                  <Typography variant="caption" color="text.secondary">Зачисление: {formatMoney(order.creditUnits, order.scale, order.currency)}</Typography>
                </TableCell>
                <TableCell><Stack spacing={1} alignItems="flex-start"><StatusChip status={scalar(order.status)} />{order.disputeState === 'OPEN' && <Chip size="small" color="warning" label="Апелляция" />}{order.requiredEvidence && <Chip size="small" label={order.requiredEvidence === 'video' ? 'Запрошено видео' : 'Запрошен чек'} />}{order.reviewRequired === true && <Chip size="small" color="warning" label="Требует проверки" />}</Stack></TableCell>
                <TableCell><Typography variant="body2">{scalar(order.documentState)}</Typography><Typography variant="caption">Подтверждение: {scalar(order.confirmationState)}</Typography></TableCell>
              </TableRow>)}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={result.total} page={query.page - 1} rowsPerPage={query.limit}
          rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="На странице"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
          onPageChange={(_, page) => setQuery(current => ({ ...current, page: page + 1 }))}
          onRowsPerPageChange={event => setQuery(current => ({ ...current, page: 1, limit: Number(event.target.value) }))}
          disabled={loading} />
      </Paper>
    </Stack>
  );
}
